import React, { useState } from 'react';

/**
 * Admin-only "Import cache snapshot" section. Renders inside RefreshPanel as
 * a collapsible advanced area. Lets the user upload a SQLite .db file built
 * locally via scripts/build-cache-bootstrap.ps1 and POST it to
 * /api/cache/import. Used to bypass EFS write latency for large backfills.
 *
 * Kept in its own file so RefreshPanel (which also handles refresh, cancel,
 * progress, facilities, etc.) doesn't grow another ~150 lines of state.
 *
 * Props:
 *   onImport         async (file, { includeFacilities, createBackup, onUploadProgress }) => result
 *   disabled         boolean — disable while a refresh or other long op is running
 */
function formatBytes(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function CacheImportSection({ onImport, disabled }) {
  const [expanded, setExpanded] = useState(false);
  const [file, setFile] = useState(null);
  const [includeFacilities, setIncludeFacilities] = useState(false);
  const [createBackup, setCreateBackup] = useState(true);
  const [importing, setImporting] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [phase, setPhase] = useState('idle'); // idle | uploading | importing | done | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setResult(null);
    setError(null);
    setPhase('idle');
    setUploadPct(0);
  };

  const handleUpload = async () => {
    if (!file || importing) return;
    setImporting(true);
    setResult(null);
    setError(null);
    setUploadPct(0);
    setPhase('uploading');
    try {
      const data = await onImport(file, {
        includeFacilities,
        createBackup,
        onUploadProgress: (pct) => {
          setUploadPct(pct);
          // Once the upload completes the server still has work to do
          // (validate schema, ATTACH, DELETE, INSERT). Flip to "importing"
          // so the user knows the spinner isn't stuck.
          if (pct >= 100) setPhase('importing');
        }
      });
      setResult(data);
      setPhase(data?.success ? 'done' : 'error');
      if (!data?.success && data?.error) setError(data.error);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.Error ||
        err?.message ||
        'Import failed';
      setError(msg);
      setPhase('error');
    } finally {
      setImporting(false);
    }
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    setError(null);
    setPhase('idle');
    setUploadPct(0);
  };

  return (
    <div className="cache-import-section">
      <button
        type="button"
        className="cache-import-toggle"
        onClick={() => setExpanded(!expanded)}
      >
        <span className="cache-import-chevron">{expanded ? '▾' : '▸'}</span>
        Import cache snapshot (advanced)
      </button>

      {expanded && (
        <div className="cache-import-body">
          <p className="cache-import-help">
            Upload a SQLite snapshot built locally via{' '}
            <code>scripts/build-cache-bootstrap.ps1</code> to bypass EFS write
            latency for large backfills. The server validates EF schema parity
            against this DB before swapping the cached tables in a single
            transaction. See <code>CACHE-BOOTSTRAP.md</code> for the full
            workflow.
          </p>

          <div className="cache-import-row">
            <input
              type="file"
              accept=".db,.sqlite,application/octet-stream"
              onChange={handleFileChange}
              disabled={importing || disabled}
            />
            {file && (
              <span className="cache-import-file-meta">
                {file.name} · {formatBytes(file.size)}
              </span>
            )}
          </div>

          <div className="cache-import-options">
            <label>
              <input
                type="checkbox"
                checked={createBackup}
                onChange={(e) => setCreateBackup(e.target.checked)}
                disabled={importing || disabled}
              />
              Create backup tables (recommended) — copies live tables to{' '}
              <code>*_backup_&lt;ts&gt;</code> before delete
            </label>
            <label>
              <input
                type="checkbox"
                checked={includeFacilities}
                onChange={(e) => setIncludeFacilities(e.target.checked)}
                disabled={importing || disabled}
              />
              Also replace <code>CachedFacilities</code> (off by default — keeps
              your last Salesforce sync)
            </label>
          </div>

          <div className="cache-import-actions">
            <button
              type="button"
              className="refresh-btn primary"
              onClick={handleUpload}
              disabled={!file || importing || disabled}
            >
              {importing
                ? phase === 'uploading'
                  ? `Uploading ${uploadPct}%`
                  : 'Importing on server…'
                : 'Upload & import'}
            </button>
            {(result || error) && !importing && (
              <button type="button" className="refresh-btn secondary" onClick={reset}>
                Reset
              </button>
            )}
          </div>

          {importing && phase === 'uploading' && (
            <div className="refresh-progress">
              <div className="progress-bar-track">
                <div className="progress-bar-fill" style={{ width: `${uploadPct}%` }} />
              </div>
              <div className="progress-label">Uploading snapshot ({uploadPct}%)</div>
            </div>
          )}
          {importing && phase === 'importing' && (
            <div className="refresh-progress">
              <div className="progress-bar-track">
                <div className="progress-bar-fill" style={{ width: '100%' }} />
              </div>
              <div className="progress-label">
                Server is validating schema and swapping cached tables…
              </div>
            </div>
          )}

          {error && (
            <div className="cache-import-result cache-import-error">
              <strong>Import failed.</strong> {error}
            </div>
          )}

          {result?.success && (
            <div className="cache-import-result cache-import-success">
              <div>
                <strong>Imported</strong> in {(result.elapsedMs / 1000).toFixed(1)}s
                {result.sourceMigrationHead && (
                  <>
                    {' '}· schema head <code>{result.sourceMigrationHead}</code>
                  </>
                )}
              </div>
              <ul className="cache-import-table-list">
                {Object.entries(result.tables || {}).map(([t, stats]) => (
                  <li key={t}>
                    <code>{t}</code>: {stats.rowsBefore.toLocaleString()} →{' '}
                    {stats.rowsAfter.toLocaleString()} ({stats.rowsInserted.toLocaleString()} inserted)
                  </li>
                ))}
              </ul>
              {result.backupSuffix && (
                <div className="cache-import-backup-hint">
                  Backup tables saved with suffix <code>{result.backupSuffix}</code>.
                  Drop them once verified:{' '}
                  <code>DROP TABLE CachedVitalEngineRecords_backup_{result.backupSuffix};</code>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CacheImportSection;
